import { useEffect, useState } from "react";
import Topbar from "./Topbar";
import BottomNav from "./BottomNav";
import NewBacktestModal from "./backtest/NewBacktestModal";
import GoLiveModal from "./live/GoLiveModal";
import { useUiStore } from "@/stores/ui";
import { useApi } from "../context/ApiContext";
import { useNavigate, useLocation } from "react-router-dom";

export default function Shell({ children }: { children: React.ReactNode }) {
  const api = useApi(["backtests"]);
  const nav = useNavigate();
  const loc = useLocation();
  const [waited, setWaited] = useState(false);
  const newBacktestOpen = useUiStore((st) => st.newBacktestOpen);
  const closeNewBacktest = useUiStore((st) => st.closeNewBacktest);
  const goLive = useUiStore((st) => st.goLive);
  const closeGoLive = useUiStore((st) => st.closeGoLive);

  useEffect(() => {
    if (api.authStatus !== "guest") return;
    const next = `${loc.pathname}${loc.search}`;
    nav(`/login?next=${encodeURIComponent(next)}`, { replace: true });
  }, [api.authStatus, loc.pathname, loc.search, nav]);

  useEffect(() => {
    if (api.authStatus !== "unknown") {
      setWaited(false);
      return;
    }
    const t = setTimeout(() => setWaited(true), 8000);
    return () => clearTimeout(t);
  }, [api.authStatus]);

  if (api.authStatus === "unknown") {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-3 bg-[var(--bg-app)] px-4 text-center text-sm text-[var(--text-secondary)]">
        <div>{waited ? api.sessionError || "Can't reach the identity service." : "Restoring session…"}</div>
        {waited && (
          <button type="button" className="btn btn-primary" onClick={() => api.reconnect()}>
            Retry
          </button>
        )}
      </div>
    );
  }

  if (api.authStatus !== "authenticated") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[var(--bg-app)] text-sm text-[var(--text-secondary)]">
        Redirecting to login…
      </div>
    );
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-[var(--bg-app)]">
      {/* The main menu lives in the top bar; the page takes the full width. */}
      <div className="flex min-h-0 min-w-0 flex-1">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <Topbar />

          <main
            className={
              loc.pathname.match(/^\/lab\/backtest\/[^/]+$/)
                ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-0"
                : loc.pathname === "/lab/backtest/new" ||
                    loc.pathname.match(/^\/lab\/backtest\/[^/]+\/edit$/)
                  ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-3 md:px-4 md:py-4"
                  : "min-h-0 min-w-0 flex-1 overflow-auto p-3 md:p-6"
            }
          >
            {api.dataError && <div role="alert" className="mb-3 flex items-center gap-3 rounded-lg bg-[var(--danger-bg)] p-3 text-sm text-[var(--danger)]"><span>{api.dataError}</span><button className="btn btn-soft btn-sm" onClick={api.reconnect}>Retry</button></div>}
            {children}
          </main>
        </div>
      </div>

      <BottomNav />
      {newBacktestOpen && <NewBacktestModal onClose={closeNewBacktest} />}
      {goLive && <GoLiveModal backtestId={goLive.backtestId} onClose={closeGoLive} />}
    </div>
  );
}
